import { useCallback, useRef } from 'react'
import { useTouchDevice } from './useTouchDevice'

interface TouchFeedbackOptions {
  /**
   * Enable active state scaling (default: true)
   */
  enableScaling?: boolean
  /**
   * Scale factor when active (default: 0.95)
   */
  scaleFactor?: number
  /**
   * Enable ripple effect (default: false)
   */
  enableRipple?: boolean
  /**
   * Disable hover interactions on touch devices (default: true)
   */
  disableHoverOnTouch?: boolean
}

/**
 * Hook to provide touch feedback for interactive elements
 * Implements active state scaling and optional ripple effects
 */
export const useTouchFeedback = (options: TouchFeedbackOptions = {}) => {
  const {
    enableScaling = true,
    scaleFactor = 0.95,
    enableRipple = false,
    disableHoverOnTouch = true
  } = options

  const isTouchDevice = useTouchDevice()
  const rippleRef = useRef<HTMLDivElement | null>(null)

  /**
   * Get CSS classes for touch feedback
   */
  const getTouchFeedbackClasses = useCallback(() => {
    const classes: string[] = []

    // Add active state scaling
    if (enableScaling) {
      classes.push('active:scale-95')
    }

    // Disable hover on touch devices
    if (disableHoverOnTouch && isTouchDevice) {
      classes.push('hover:none')
    }

    // Add transition for smooth feedback
    classes.push('transition-transform duration-150 ease-out')

    return classes.join(' ')
  }, [enableScaling, disableHoverOnTouch, isTouchDevice])

  /**
   * Create ripple effect at touch point
   */
  const createRipple = useCallback((event: React.TouchEvent | React.MouseEvent) => {
    if (!enableRipple) return

    const button = event.currentTarget as HTMLElement
    const rect = button.getBoundingClientRect()

    // Get touch/click position
    let x: number, y: number
    if ('touches' in event) {
      x = event.touches[0].clientX - rect.left
      y = event.touches[0].clientY - rect.top
    } else {
      x = event.clientX - rect.left
      y = event.clientY - rect.top
    }

    // Create ripple element
    const ripple = document.createElement('span')
    ripple.className = 'absolute rounded-full bg-white/30 pointer-events-none animate-ripple'
    ripple.style.width = ripple.style.height = '0px'
    ripple.style.left = `${x}px`
    ripple.style.top = `${y}px`
    ripple.style.transform = 'translate(-50%, -50%)'

    // Add to button
    button.style.position = 'relative'
    button.style.overflow = 'hidden'
    button.appendChild(ripple)

    // Animate ripple
    requestAnimationFrame(() => {
      const size = Math.max(rect.width, rect.height) * 2
      ripple.style.width = ripple.style.height = `${size}px`
      ripple.style.opacity = '0'
      ripple.style.transition = 'width 0.6s ease-out, height 0.6s ease-out, opacity 0.6s ease-out'
    })

    // Remove ripple after animation
    setTimeout(() => {
      ripple.remove()
    }, 600)
  }, [enableRipple])

  /**
   * Handle touch start event
   */
  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    createRipple(event)
  }, [createRipple])

  /**
   * Handle mouse down event (for non-touch devices)
   */
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!isTouchDevice) {
      createRipple(event)
    }
  }, [isTouchDevice, createRipple])

  return {
    isTouchDevice,
    touchFeedbackClasses: getTouchFeedbackClasses(),
    touchHandlers: enableRipple ? {
      onTouchStart: handleTouchStart,
      onMouseDown: handleMouseDown
    } : {},
    shouldDisableHover: disableHoverOnTouch && isTouchDevice
  }
}
